import React, { useState, useEffect } from 'react';
import { TextField, Checkbox, FormControlLabel, Select, FormControl, InputLabel, MenuItem } from '@mui/material';
import { TextareaAutosize as BaseTextareaAutosize } from '@mui/base/TextareaAutosize';

import "dayjs/locale/fr";
import dayjs from 'dayjs';

import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { countriesFr } from '../helpers/countriesFr'

import { focusNextInput, generateId } from '../scripts/utils';

import { styled } from '@mui/system';

import { useData } from '../hooks/DataProvider';


const { TransportMode } = require('../scripts/configData');

const TextareaAutosize = styled(BaseTextareaAutosize)(
    ({ theme }) => `
    // box-sizing: border-box;
    // width: 320px;
    // font-family: 'IBM Plex Sans', sans-serif;
    // font-size: 0.875rem;
    // font-weight: 400;
    // line-height: 1.5;
    // padding: 8px 12px;
    // border-radius: 8px;


    /* firefox */
    &:focus-visible {
      outline: 0;
    }
  `,
);

const StringField = ({ labelName, id, value, onChange, required, disabled, disabledInForm }) => {
    return (
        <TextField
            label={labelName}
            id={`${id}_${generateId()}`}
            value={value || ''}
            onChange={onChange}
            required={required}
            size="small"
            autoComplete="off"
            disabled={disabled || disabledInForm || false}
            slotProps={{ inputLabel: { shrink: true } }}
        />
    );
};

const FloatField = ({ labelName, id, value, onChange, required, disabled, disabledInForm, errorCondition }) => {
    const [inputValue, setInputValue] = useState(() => {
        if (typeof value === 'number') {
            return value.toLocaleString('en-EN', { minimumFractionDigits: 0, useGrouping: false });
        }
        return value || '';
    });

    useEffect(() => {
        if (typeof value === 'number') {
            setInputValue(value.toLocaleString('en-EN', { minimumFractionDigits: 0, useGrouping: false }));
        } else if (value === 0) {
            setInputValue('0');
        }
    }, [value]);

    const handleBlur = () => {
        const normalized = inputValue.replace(',', '.');
        const parsed = parseFloat(normalized);
        const finalValue = isNaN(parsed) ? 0 : parsed;
        onChange({ target: { id, value: finalValue } });
    };

    const handleChange = (event) => {
        let raw = event.target.value;

        // Разрешаем только: цифры, точку, запятую и минус в начале
        raw = raw.replace(/[^0-9,.\-]/g, '');

        // Удаляем все минусы кроме первого
        const firstMinus = raw.startsWith('-') ? '-' : '';
        raw = firstMinus + raw.replace(/-/g, '');

        setInputValue(raw);
    };

    return (
        <TextField
            label={labelName}
            id={`${id}_${generateId()}`}
            value={inputValue}
            onChange={handleChange}
            onBlur={handleBlur}
            required={required}
            size="small"
            type="text"
            onFocus={event => event.target.select()}
            onKeyUp={event => { if (event.key === 'Enter') focusNextInput(); }}
            autoComplete="off"
            disabled={disabled || disabledInForm || false}
            inputMode="decimal"
            error={errorCondition}
        />
    );
};

const BooleanField = ({ labelName, id, value, onChange, required, disabled, disabledInForm }) => {
    return (
        <FormControlLabel
            control={
                <Checkbox
                    checked={value || false}
                    onChange={onChange}
                    required={required}
                    id={`${id}_${generateId()}`}
                    disabled={disabled || disabledInForm || false}
                />
            }
            label={labelName}
            slotProps={{ typography: { noWrap: true } }}
        />
    );
};

const TextareaField = ({ labelName, id, value, onChange, required, disabled, disabledInForm, minRows = 3 }) => {
    return (
        <TextareaAutosize
            id={`${id}_${generateId()}`}
            value={value || ''}
            onChange={onChange}
            required={required}
            label={labelName}
            minRows={minRows}
            style={{
                border: '1px solid #ccc',
                borderRadius: '4px',
                padding: '8px',
            }}
            placeholder={labelName}
            disabled={disabled || disabledInForm || false}
        />
    );
};

const SelectField = ({ labelName, id, value, onChange, required, disabled, disabledInForm, options, language }) => {
    return (
        <FormControl fullWidth>
            <InputLabel
                id={`label_${id}_${generateId()}`}
                sx={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}
                size="small"
            >{labelName}</InputLabel>
            <Select
                labelId={`label_${id}`}
                id={`label_${id}_${generateId()}`}
                value={value || ''}
                onChange={(e) => onChange({ target: { id, value: e.target.value } })}
                required={required}
                variant='outlined'
                size="small"
                autoComplete="off"
                label={labelName}
                disabled={disabled || disabledInForm || false}
            >
                {options.map((option) => (
                    <MenuItem key={option.key} value={option.key}>
                        {option[language]}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

const DataField = ({ labelName, id, value, onChange, required, disabled, disabledInForm, language, defaultValue }) => {
    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={language === 'fr' ? 'fr' : 'en'}>
            <DatePicker
                label={labelName}
                id={`${id}_${generateId()}`}
                value={value ? dayjs(value) : defaultValue === 'now' ? dayjs().format('YYYY-MM-DD') : null}
                onChange={(e) => onChange({ target: { id, value: e } })}
                slotProps={{
                    textField: {
                        size: 'small',
                        
                        InputLabelProps: { shrink: true }
                    },
                }}
                disabled={disabled || disabledInForm || false}
            />
        </LocalizationProvider>
    );
};


const CalculateVolume = (hu, mainData) => {


    // if (hu?.huLength && hu?.huWidth && hu?.huHeight && hu?.huWeight) {

    let huVolume = 0;

    if (hu.huLength && hu.huWidth && hu.huHeight) {
        huVolume = (hu.huLength * hu.huWidth * hu.huHeight / 1000000).toFixed(2); // Convert cm^3 to m^3
    }

    const huLinearMeters = (hu.huLength * hu.huWidth / 10000 / 2.4).toFixed(2); // Convert cm to meters

    const huWeightToVolumeRatio = (hu.huWeight / huVolume).toFixed(2);

    const volumeWeightRatio = TransportMode.find(tm => tm.key === mainData.transportMode)['volumeWeightRatio'];
    const huChargeableWeight = Math.max(hu.huWeight, huVolume * volumeWeightRatio).toFixed(2); // Assuming 166.67 is the conversion factor for chargeable weight


    hu.huVolume = huVolume;
    hu.huLinearMeters = huLinearMeters
    hu.huWeightToVolumeRatio = huWeightToVolumeRatio
    hu.huChargeableWeight = huChargeableWeight


    return hu;
    // }
}



// CreatFieldComponent: React component version of CreatField
const CreatFieldComponent = ({ field = {}, data = {}, updateData = () => { }, disabledInForm = false, options = [{}], fn = () => { }, fnParams = null, mainData = {}, errorCondition = false}) => {
    const { dataShipement, updateDataShipement,
        dataServiceList, updateDataServiceList, language } = useData();

    let computedData = data;
    if (computedData?.huWeight && computedData?.huLength && computedData?.huWidth && computedData?.huHeight && mainData?.transportMode) {
        computedData = CalculateVolume(computedData, mainData);
    }

    let struct = {
        ...field,
        value: computedData[field.id] || field.default,
        labelName: field[language],
        language: language,
        disabledInForm: disabledInForm,
        errorCondition: errorCondition,
        options: options,
        onChange: async (e) => {
            let fld = e.target.id
            if (e.target.id.includes('_')) {
                fld = e.target.id.split('_')[0]
            }

            if (field['typeof'] === 'boolean') {
                updateData({ ...computedData, [fld]: e.target.checked });
            } else if (field['typeof'] === 'select') {
                const value = e.target.value;
                updateData({ ...computedData, [fld]: value });
            } else if (field['typeof'] === 'numberFloat') {
                const value = parseFloat(e.target.value); // Ensure two decimal places
                    updateData({ ...computedData, [fld]: value });
            } else if (field['typeof'] === 'date') {
                const value = e.target.value.format('YYYY-MM-DD');
                updateData({ ...computedData, [fld]: value });
            }
            else if (fld === 'countryCode') {
                let cc = e.target.value.toLocaleUpperCase()
                let country = countriesFr.find(c => c.code.toLocaleUpperCase() === cc);
                if (country !== undefined) {
                    updateData({ ...computedData, countryCode: e.target.value.toLocaleUpperCase(), country: country.name })
                } else {
                    updateData({ ...computedData, countryCode: e.target.value.toLocaleUpperCase() })
                }
            } else {
                if (fld) updateData({ ...computedData, [fld]: e.target.value })
            }
        }
    };

    switch (field['typeof']) {
        case '_id':
            return null;
        case 'string':
            return StringField(struct);
        case 'numberFloat':
            return FloatField(struct);
        case 'boolean':
            return BooleanField(struct);
        case 'array':
            return 'array';
        case 'textarea':
            return TextareaField(struct);
        case 'select':
            return SelectField(struct);
        case 'date':
            return DataField(struct);
        default:
            return 'Unknown';
    }
};


export { CreatFieldComponent };